import { DeleteOutlined, UserOutlined } from "@ant-design/icons"
import {
  Alert,
  Button,
  Empty,
  Flex,
  Select,
  Skeleton,
  Tag,
  Typography
} from "antd"
import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState
} from "react"
import { useTranslation } from "react-i18next"

import { neutral } from "../../../../components/widgets/tokens.ts"
import { getEntityName } from "../../../../utils/user/get-entity-name.ts"
import { getReportShares, updateReportShares } from "../../api/services.ts"
import {
  editorEntriesToPayload,
  shareSaveErrorMessage,
  type ShareEditorEntry,
  sharesToEditorEntries
} from "../../helpers/reportSharing.ts"
import type { ReportShareLevel } from "../../types/collab.types.ts"
import type { ReportOwner } from "../../types/types.ts"
import { ReportSharePeoplePicker } from "../ReportSharePeoplePicker.tsx"

const { Text, Title } = Typography

export interface ReportWizardShareHandle {
  save: () => Promise<boolean>
}

interface ReportWizardStepShareProps {
  reportId: string
  owner: ReportOwner
  entries: ShareEditorEntry[] | null
  onEntriesChange: (entries: ShareEditorEntry[]) => void
}

// The wizard owns `entries`: this step unmounts on leave but only PUTs on Finish.
export const ReportWizardStepShare = forwardRef<
  ReportWizardShareHandle,
  ReportWizardStepShareProps
>(({ reportId, owner, entries, onEntriesChange }, ref) => {
  const { t } = useTranslation()

  const [saveError, setSaveError] = useState<string | null>(null)
  const loaded = entries !== null

  useEffect(() => {
    if (loaded) return
    let cancelled = false
    void (async () => {
      const response = await getReportShares(reportId)
      if (cancelled) return
      onEntriesChange(
        response.ok && response.data
          ? sharesToEditorEntries(response.data.shares)
          : []
      )
    })()
    return () => {
      cancelled = true
    }
  }, [reportId, loaded, onEntriesChange])

  useImperativeHandle(ref, () => ({
    save: async () => {
      setSaveError(null)
      const response = await updateReportShares(reportId, {
        shares: editorEntriesToPayload(entries ?? [])
      })
      if (!response.ok) {
        setSaveError(shareSaveErrorMessage(response) ?? t("reports.share.saveError"))
      }
      return response.ok
    }
  }))

  const levelOptions = useMemo(
    () => [
      { label: t("reports.share.levels.view"), value: "view" },
      { label: t("reports.share.levels.edit"), value: "edit" }
    ],
    [t]
  )

  const addEntry = (entry: ShareEditorEntry) =>
    onEntriesChange([...(entries ?? []), entry])

  const changeLevel = (key: string, level: ReportShareLevel) =>
    onEntriesChange(
      (entries ?? []).map((entry) =>
        entry.key === key ? { ...entry, level } : entry
      )
    )

  const removeEntry = (key: string) =>
    onEntriesChange((entries ?? []).filter((entry) => entry.key !== key))

  if (!entries) return <Skeleton active paragraph={{ rows: 5 }} />

  return (
    <Flex vertical gap="large" style={{ maxWidth: 620 }}>
      <Flex vertical gap={2}>
        <Title level={4} className="m-0">
          {t("reports.wizard.share.title")}
        </Title>
        <Text type="secondary">{t("reports.wizard.share.subtitle")}</Text>
      </Flex>

      {saveError && (
        <Alert
          type="error"
          showIcon
          message={saveError}
          closable
          onClose={() => setSaveError(null)}
        />
      )}

      <Flex vertical gap="small">
        <Text strong>{t("reports.share.people")}</Text>
        <ReportSharePeoplePicker
          reportId={reportId}
          owner={owner}
          entries={entries}
          onAdd={addEntry}
          className="w-full"
        />
      </Flex>

      <Flex vertical gap="small">
        <Flex
          align="center"
          justify="space-between"
          gap="small"
          style={{
            padding: "6px 4px",
            borderBottom: `1px solid ${neutral.border}`
          }}
        >
          <Flex align="center" gap="small" className="min-w-0">
            <UserOutlined />
            <Text ellipsis>{getEntityName(owner)}</Text>
          </Flex>
          <Tag>{t("reports.access.owner")}</Tag>
        </Flex>

        {entries.length === 0 ? (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={t("reports.share.emptyPeople")}
          />
        ) : (
          entries.map((entry) => (
            <Flex
              key={entry.key}
              align="center"
              justify="space-between"
              gap="small"
              style={{ padding: "2px 4px" }}
            >
              <Flex align="center" gap="small" className="min-w-0">
                <UserOutlined />
                <Text ellipsis>{entry.name}</Text>
                {entry.kind === "role" && (
                  <Tag color="blue">{t("reports.share.role")}</Tag>
                )}
              </Flex>
              <Flex align="center" gap="small">
                <Select<ReportShareLevel>
                  size="small"
                  value={entry.level}
                  options={levelOptions}
                  onChange={(level) => changeLevel(entry.key, level)}
                  style={{ width: 96 }}
                  aria-label={t("reports.share.levelFor", { name: entry.name })}
                />
                <Button
                  type="text"
                  size="small"
                  danger
                  icon={<DeleteOutlined />}
                  aria-label={t("reports.share.remove", { name: entry.name })}
                  onClick={() => removeEntry(entry.key)}
                />
              </Flex>
            </Flex>
          ))
        )}
      </Flex>
    </Flex>
  )
})

ReportWizardStepShare.displayName = "ReportWizardStepShare"
